import { Button } from '@monorepo/shared/componentsV2/Button';
import { ErrorBoundary } from '@monorepo/shared/componentsV2/ErrorBoundary';
import { useDataset } from '@monorepo/shared/hooks/datasets/useDataset';
import AddIcon from '@svg/add.svg';
import { JoinStepConditionRequest, WorkflowResponse } from 'mapistry-shared';
import React, { useCallback, useMemo, useRef } from 'react';
import { useField } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import styled from 'styled-components';
import { findStepInputDataset } from '../stepUtils';
import { DateIntervalCondition } from './DateIntervalCondition';
import {
  areAllRegularConditionsOptional,
  doesNeedDateIntervalCondition,
  getTimeSeriesJoinType,
  isDefaultProjectConditionEnough,
  isEffectivePeriodConditionRelevant,
} from './helpers';
import { JoinOnResourceTypeCallToAction } from './JoinOnResourceTypeCallToAction';
import { ProjectCondition } from './ProjectCondition';
import { StepCondition } from './StepCondition';
import {
  StyledConditionsContainer,
  StyledDescription,
  StyledH3,
} from './styled';
import { EffectivePeriodCondition } from './EffectivePeriodCondition';

const StyledDateIntervalCondition = styled(DateIntervalCondition)`
  margin-bottom: 1rem;
`;

const StyledProjectCondition = styled(ProjectCondition)`
  margin-bottom: 1rem;
`;

const StyledEffectivePeriodCondition = styled(EffectivePeriodCondition)`
  margin-bottom: 1rem;
`;

interface JoinConditionsModalPageProps {
  workflow: WorkflowResponse;
  stepId?: string;
}

function JoinConditionsModalPageContent({
  stepId,
  workflow,
}: JoinConditionsModalPageProps) {
  const {
    input: { value: selectedJoinToDatasetId },
  } = useField<string>('joinToDatasetId');

  const { dataset: joinToDataset } = useDataset({
    organizationId: workflow.organizationId,
    datasetId: selectedJoinToDatasetId,
  });
  const stepInputDataset = useMemo(
    () => findStepInputDataset(workflow, stepId),
    [stepId, workflow],
  );
  const timeSeriesJoinType = useMemo(
    () =>
      joinToDataset && getTimeSeriesJoinType(stepInputDataset, joinToDataset),
    [stepInputDataset, joinToDataset],
  );
  const doesNeedDateCondition = useMemo(
    () =>
      !!timeSeriesJoinType && doesNeedDateIntervalCondition(timeSeriesJoinType),
    [timeSeriesJoinType],
  );
  const areAllConditionsOptional = useMemo(
    () =>
      !!timeSeriesJoinType &&
      !!joinToDataset &&
      areAllRegularConditionsOptional(
        timeSeriesJoinType,
        stepInputDataset,
        joinToDataset,
      ),
    [joinToDataset, stepInputDataset, timeSeriesJoinType],
  );
  const isProjectConditionVisible = useMemo(
    () =>
      !!timeSeriesJoinType &&
      !!joinToDataset &&
      isDefaultProjectConditionEnough(
        timeSeriesJoinType,
        stepInputDataset,
        joinToDataset,
      ),
    [joinToDataset, stepInputDataset, timeSeriesJoinType],
  );

  const isEffectivePeriodConditionVisible = useMemo(
    () =>
      !!joinToDataset &&
      isEffectivePeriodConditionRelevant(stepInputDataset, joinToDataset),
    [joinToDataset, stepInputDataset],
  );

  /**
   * move focus to the add button if it would get lost otherwise
   * (e.g. if we're deleting the last condition in the list, or there are only 2 left and we delete one)
   */
  const addConditionButtonRef = useRef<HTMLButtonElement>(null);
  const preventLosingFocus = useCallback((conditions, idx) => {
    if (conditions.length === 2 || idx + 1 === conditions.length) {
      addConditionButtonRef.current?.focus();
    }
  }, []);

  return (
    <>
      <StyledH3>Match fields</StyledH3>
      <StyledDescription>
        Choose which fields you’d like to match from each of your data sources.
      </StyledDescription>

      {doesNeedDateCondition && joinToDataset && (
        <StyledDateIntervalCondition
          leftDataset={stepInputDataset}
          rightDataset={joinToDataset}
        />
      )}

      {isProjectConditionVisible && <StyledProjectCondition />}

      {isEffectivePeriodConditionVisible && (
        <StyledEffectivePeriodCondition inputDataset={stepInputDataset} />
      )}

      <FieldArray<Partial<JoinStepConditionRequest>> name="conditions">
        {({ fields: conditions }) => (
          <div>
            {!!conditions.length && conditions.length > 0 && (
              <StyledConditionsContainer>
                {conditions.map((conditionName, idx) => (
                  <StepCondition
                    canDelete={
                      areAllConditionsOptional || conditions.value.length > 1
                    }
                    formFieldName={conditionName}
                    key={conditionName}
                    leftDataset={stepInputDataset}
                    rightDataset={joinToDataset}
                    shouldLabelShow={idx === 0}
                    onDelete={() => {
                      preventLosingFocus(conditions, idx);
                      conditions.remove(idx);
                    }}
                    setCondition={(
                      leftPropertyId: string,
                      rightPropertyId: string | undefined,
                    ) => {
                      conditions.update(idx, {
                        leftPropertyId,
                        rightPropertyId,
                      });
                    }}
                  />
                ))}
              </StyledConditionsContainer>
            )}

            <Button
              ref={addConditionButtonRef}
              color="primary"
              onClick={() => {
                conditions.push({
                  leftPropertyId: undefined,
                  rightPropertyId: undefined,
                });
              }}
            >
              <AddIcon />
              Add another
            </Button>
            {joinToDataset && (
              <JoinOnResourceTypeCallToAction
                stepInputDataset={stepInputDataset}
                joinToDataset={joinToDataset}
                conditions={conditions}
              />
            )}
          </div>
        )}
      </FieldArray>
    </>
  );
}

export function JoinConditionsModalPage(props: JoinConditionsModalPageProps) {
  return (
    <ErrorBoundary>
      <JoinConditionsModalPageContent {...props} />
    </ErrorBoundary>
  );
}
